
import React, { useMemo } from 'react';
import { Card } from "@/components/ui/card";
import { Grid3X3 } from 'lucide-react';
import type { PerformanceData } from './types';
import { useIsMobile } from '@/hooks/use-mobile';

interface KnowledgeMasteryHeatmapProps {
  performanceData: PerformanceData[];
}

const KnowledgeMasteryHeatmap: React.FC<KnowledgeMasteryHeatmapProps> = ({ 
  performanceData 
}) => {
  const { isMobile, screenSize } = useIsMobile();
  
  const masteryData = useMemo(() => {
    // Process the performance data to extract mastery by topic/attempt
    const topicData: Record<string, Record<string, number>> = {};
    const attempts: string[] = [];

    // First, group by subject and sort chronologically
    const subjectsData = performanceData.reduce((acc, entry) => {
      if (!entry.subject) return acc;
      
      if (!acc[entry.subject]) {
        acc[entry.subject] = [];
      }
      
      acc[entry.subject].push(entry);
      return acc;
    }, {} as Record<string, PerformanceData[]>);
    
    // For each subject, track attempts chronologically - but limit to 5 most recent
    Object.entries(subjectsData).forEach(([subject, entries]) => {
      // Sort entries by date (most recent first)
      const sortedEntries = [...entries].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      // Take only the 5 most recent entries
      const recentEntries = sortedEntries.slice(0, 5);
      
      // Reverse to display chronologically (earliest to latest)
      recentEntries.reverse().forEach((entry, index) => {
        const attemptKey = `Attempt ${index + 1}`;
        
        if (!attempts.includes(attemptKey)) {
          attempts.push(attemptKey);
        }
        
        if (!topicData[subject]) {
          topicData[subject] = {};
        }
        
        // Calculate mastery score as percentage of correct answers
        if (entry.total_questions > 0) {
          const masteryScore = (entry.correct_answers / entry.total_questions) * 100;
          topicData[subject][attemptKey] = Math.round(masteryScore);
        }
      });
    });

    return { topicData, attempts: attempts.slice(0, 5) }; // Ensure only 5 attempts max
  }, [performanceData]);

  const getColorForMastery = (mastery: number) => {
    if (mastery >= 80) return 'bg-green-500';
    if (mastery >= 60) return 'bg-yellow-500';
    if (mastery >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getMasteryLabel = (mastery: number) => {
    if (mastery >= 80) return 'Expert';
    if (mastery >= 60) return 'Proficient';
    if (mastery >= 40) return 'Developing';
    return 'Beginner';
  };

  return (
    <Card className={`${isMobile ? "p-3" : "p-6"} shadow-md`}>
      <h2 className={`${isMobile ? "text-base" : "text-xl"} font-semibold mb-4 flex items-center gap-2`}>
        <Grid3X3 className="w-5 h-5 text-purple-500" />
        Knowledge Mastery Heatmap
      </h2>
      
      {Object.keys(masteryData.topicData).length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Grid3X3 className="w-12 h-12 text-gray-300 mb-4" />
          <p className="text-gray-500 text-center mb-2">No mastery data available yet</p>
          <p className="text-sm text-gray-400 text-center max-w-md">
            Complete quizzes across different topics to visualize your knowledge mastery
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-3">
            <div className={`${isMobile ? "px-3 pb-1" : ""} w-full`}>
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    <th className={`${isMobile ? "p-1 text-xs" : "p-2"} border text-left font-medium`}>Topic</th>
                    {masteryData.attempts.map(attempt => (
                      <th key={attempt} className={`${isMobile ? "p-1 text-xs" : "p-2 text-sm"} border font-medium text-center`}>
                        {attempt}
                      </th>
                    ))}
                    <th className={`${isMobile ? "p-1 text-xs" : "p-2 text-sm"} border font-medium text-center`}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(masteryData.topicData).map(([topic, attemptScores]) => {
                    const lastAttempt = masteryData.attempts[masteryData.attempts.length - 1];
                    const lastScore = attemptScores[lastAttempt] || 0;
                    
                    return (
                      <tr key={topic}>
                        <td className={`${isMobile ? "p-1 text-xs" : "p-2"} border font-medium`}>{topic}</td>
                        {masteryData.attempts.map(attempt => {
                          const score = attemptScores[attempt];
                          return (
                            <td key={`${topic}-${attempt}`} className={`${isMobile ? "p-1" : "p-2"} border`}>
                              <div className="relative">
                                <div
                                  className={`w-full ${isMobile ? "h-6" : "h-8"} ${score ? getColorForMastery(score) : 'bg-gray-200'} opacity-50 rounded`}
                                ></div>
                                <div className={`absolute inset-0 flex items-center justify-center ${isMobile ? "text-xs" : "text-sm"} font-medium`}>
                                  {score !== undefined ? `${score}%` : 'N/A'}
                                </div>
                              </div>
                            </td>
                          );
                        })}
                        <td className={`${isMobile ? "p-1 text-xs" : "p-2 text-sm"} border text-center`}>
                          {lastScore > 0 ? (
                            <span className={`${isMobile ? "text-xs" : "text-sm"} font-medium`}>
                              {getMasteryLabel(lastScore)}
                            </span>
                          ) : (
                            <span className={`${isMobile ? "text-xs" : "text-sm"} text-gray-500`}>No data</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className={`mt-4 flex flex-wrap ${isMobile ? "justify-center gap-2 text-[10px]" : "justify-end gap-3 text-xs"}`}>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 opacity-50 rounded"></div>
              <span>Beginner (0-40%)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-orange-500 opacity-50 rounded"></div>
              <span>Developing (41-60%)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-500 opacity-50 rounded"></div>
              <span>Proficient (61-80%)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 opacity-50 rounded"></div>
              <span>Expert (81-100%)</span>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default KnowledgeMasteryHeatmap;
